// src/pages/dashboard/branch/menu-management/MenuManagementPage.tsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FiAward, FiLayers, FiLink, FiMapPin } from 'react-icons/fi';
import { TbCarrot } from 'react-icons/tb';
import { MdCampaign } from 'react-icons/md';
import { useParams } from 'react-router-dom';
import IngredientSettings from '../settings/componenets/IngredientSettings';
import DietaryPreferencesPage from './dietary-preferences/DietaryPreferencesPage';
import VariantManagementPage from '../variants/VariantManagementPage';
import CrossSellingPage from './cross-selling/CrossSellingPage';
import ZonesPage from './zones/ZonesPage';
import AnnouncementsPage from './announcements/AnnouncementsPage';

const MenuManagementPage: React.FC = () => {
    const { t } = useTranslation();
    const { branchId } = useParams<{ branchId: string }>();
    const [activeTab, setActiveTab] = useState('announcements');

    const tabs = [
         {
            id: 'announcements',
            label: 'menuManagement.tabs.announcements',
            icon: MdCampaign,
            title: t('menuManagement.tabs.announcements', 'Announcements'),
            subtitle: t('menuManagement.announcements.subtitle', 'Manage announcements'),
            color: 'from-amber-500 to-orange-600'
        },
        {
            id: 'variants',
            label: 'menuManagement.tabs.variants',
            icon: FiLayers,
            title: t('menuManagement.tabs.variants', 'Variants'),
            subtitle: t('menuManagement.variants.subtitle', 'Manage variants of menu items'),
            color: 'from-blue-500 to-indigo-600'
        },
        {
            id: 'ingredients',
            label: 'menuManagement.tabs.ingredients',
            icon: TbCarrot,
            title: t('menuManagement.tabs.ingredients'),
            subtitle: t('menuManagement.ingredients.subtitle', 'Manage ingredients used in menu items'),
            color: 'from-blue-500 to-indigo-600'
        },
        {
            id: 'dietary',
            label: 'menuManagement.tabs.dietaryPreferences',
            icon: FiAward,
            title: t('menuManagement.tabs.dietaryPreferences', 'Dietary Preferences'),
            subtitle: t('menuManagement.dietary.subtitle', 'Manage dietary restrictions and preferences for menu items'),
            color: 'from-green-500 to-emerald-600'
        },
        {
            id: 'crossSelling',
            label: 'menuManagement.tabs.crossSelling',
            icon: FiLink,
            title: t('menuManagement.tabs.crossSelling', 'Cross-Selling'),
            subtitle: t('menuManagement.crossSelling.subtitle', 'Manage item recommendations'),
            color: 'from-purple-500 to-pink-600'
        },
        {
            id: 'zones',
            label: 'menuManagement.tabs.zones',
            icon: FiMapPin,
            title: t('menuManagement.tabs.zones', 'Zones'),
            subtitle: t('menuManagement.zones.subtitle', 'Manage delivery zones'),
            color: 'from-amber-500 to-orange-600'
        },
       
    ];

    const renderContent = () => {
        switch (activeTab) {
            case 'announcements':
                return <AnnouncementsPage />;
            case 'dietary':
                return <DietaryPreferencesPage />;
            case 'variants':
                return <VariantManagementPage />;
            case 'ingredients':
                return (
                    <div className="p-6">
                        <IngredientSettings
                            restaurant={null}
                            updateRestaurant={() => { }}
                            branchId={branchId}
                        />
                    </div>
                );
            case 'crossSelling':
                return <CrossSellingPage />;
            case 'zones':
                return <ZonesPage />;
            default:
                return <VariantManagementPage />;
        }
    };

    return (
        <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
            {/* Header section with title and subtitle */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                        {t('menuManagement.title')}
                    </h1>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {t('menuManagement.subtitle')}
                    </p>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="mb-8">
                <div className="border-b border-gray-200 dark:border-gray-700">
                    <nav className="-mb-px flex space-x-8 overflow-x-auto">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            const isActive = activeTab === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm cursor-pointer flex items-center gap-2 ${isActive
                                        ? 'border-primary text-primary'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                                        }`}
                                >
                                    <Icon className="h-5 w-5" />
                                    <span>{tab.title}</span>
                                </button>
                            );
                        })}
                    </nav>
                </div>
            </div>

            {/* Tab Content */}
            <div key={activeTab} className="mt-6 overflow-x-hidden">
                {renderContent()}
            </div>
        </div>
    );
};

export default MenuManagementPage;